import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { getToken, setToken, clearToken } from "./token-store";

const USER_KEY = "cg_user";

interface AuthUser {
  tenant_id: string;
  actor_id: string;
  scopes: string[];
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  login: (token: string, user: AuthUser) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function loadStoredUser(): AuthUser | null {
  try {
    const raw = sessionStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

/**
 * Provides authentication state to child components.
 *
 * Stores JWT and user info in sessionStorage so they survive page reloads
 * but are cleared when the tab/browser closes. Listens for the "auth:logout"
 * custom DOM event dispatched by the API client on 401 responses.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => {
    // Rehydrate from sessionStorage on mount (survives page reload)
    const token = getToken();
    return token ? loadStoredUser() : null;
  });

  // Listen for 401 logout events dispatched by the API client
  useEffect(() => {
    const handler = () => {
      setUser(null);
      sessionStorage.removeItem(USER_KEY);
    };
    window.addEventListener("auth:logout", handler);
    return () => window.removeEventListener("auth:logout", handler);
  }, []);

  const login = useCallback((token: string, authUser: AuthUser) => {
    setToken(token);
    sessionStorage.setItem(USER_KEY, JSON.stringify(authUser));
    setUser(authUser);
  }, []);

  const logout = useCallback(() => {
    clearToken();
    sessionStorage.removeItem(USER_KEY);
    setUser(null);
    window.dispatchEvent(new CustomEvent("auth:logout"));
  }, []);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: user !== null,
      login,
      logout,
    }),
    [user, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Returns the current authentication context.
 *
 * Raises:
 *   Error: If used outside an AuthProvider.
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
